<script setup lang="ts">
import { ref, inject, type Ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { SuiButton, SuiCheckbox, SuiForm, useToast } from 'vue-fomantic-ui'
import { useContainerStore } from '@/stores/containers'
import { useRoomStore } from '@/stores/rooms'
import type { ContainerType } from '@/models/ContainerType'
import type { Room } from '@/models/Room'
import { ScanState, ScanTarget } from '@/models/Scan'
import Title from '@/components/Title.vue'
import RoomScanHeading from '@/components/Heading/RoomScanHeading.vue'
import Scanner from '@/components/Scanner/Scanner.vue'
import FadeTransition from '@/components/FadeTransition.vue'
import RoomDetailHeading from '@/components/Heading/RoomHeading.vue'

const router = useRouter()
const { toast } = useToast()

const containerStore = useContainerStore()
const roomStore = useRoomStore()

const containerType = inject('containerType') as Ref<ContainerType | null>
const containerCodes = inject('containerCodes') as Ref<string[]>
const room = inject('room') as Ref<Room | null>

const assignDefaultRoom = ref(false)
const saving = ref(false)

watch(assignDefaultRoom, () => {
  if (assignDefaultRoom.value) {
    room.value = null
  }
})

const barcodeScanned = (value: string) => {
  room.value = roomStore.findByCode(value)

  if (room.value) {
    assignDefaultRoom.value = false
  } else {
    toast({
      position: 'centered',
      message: `Le code barre scanné n'est pas celui d'un local.`,
      type: 'warning',
    })
  }
}

const save = async () => {
  if (!containerCodes.value) return

  saving.value = true

  for (const code of containerCodes.value) {
    await containerStore.addContainer(
      code,
      containerType.value as ContainerType,
      room.value || (roomStore.defaultRoom as Room),
    )
  }

  toast({
    position: 'centered',
    message: `Les bacs ont été créés.`,
    type: 'success',
  })

  containerType.value = null
  containerCodes.value = []
  room.value = null

  router.push({ name: 'add-container' })
}
</script>

<template>
  <div class="page add-container-scan">
    <Title>Ajouter des bacs</Title>
    <SuiForm @submit.prevent="save">
      <p>Sélectionner l'affectation</p>
      <div class="field">
        <SuiCheckbox label="Affecter à la plateforme logistique" v-model="assignDefaultRoom" />
      </div>
      <div class="separator">ou</div>
      <FadeTransition>
        <RoomDetailHeading v-if="room" />
        <RoomScanHeading v-else />
      </FadeTransition>
      <Scanner
        class="scanner"
        :target="ScanTarget.Room"
        :state="room ? ScanState.Scanned : ScanState.Scanning"
        @barcode-scanned="barcodeScanned"
      />
      <SuiButton class="submit" type="submit" :disabled="room === null && !assignDefaultRoom"
        >Continuer</SuiButton
      >
    </SuiForm>
  </div>
</template>

<style scoped lang="scss">
.add-container-scan {
  .ui.form {
    p,
    .field {
      width: 100%;
      margin: 1.5dvh 0;
    }
    .separator {
      margin-top: 1dvh;
      text-align: center;
      text-transform: uppercase;
    }
  }

  .scanner {
    height: 25dvh;
  }
}

/* @TODO: override Fomantic styles in a cleaner way */
.ui.form {
  min-height: 67.5dvh;
  padding: 2dvh 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}
.ui.button {
  font-family: 'Libre Franklin', sans-serif;
  padding: 8px 20px 7px 20px;
  background-color: #344c60;
  transition: opacity 0.25s;
  color: white;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0;
  height: 40px;
  width: 60%;
  line-height: 22px;
  margin: 1.5dvh 0;
}
</style>
